// libraries
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import noop from 'lodash/noop'
import { v4 as uuidv4 } from 'uuid'
import { Stack, Box, Divider, Button, Grid, Tabs, Tab } from '@mui/material'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'

// state
import { setSnackBar } from 'state/ui'
import { addScrapVehicle, setNewCustomerVehicle, updateScrapVehicle } from 'state/inboundTicket'

// queries and mutation
import { useGetVehicleInfoFromVinQuery } from 'services/customers'
import { useCreateVehicleMutation } from 'services/vehicles'

// specific components
import VehicleFormLayout from 'components/layouts/VehicleFormLayout'

// generic components
import Loading from 'components/generic/Loading'
import FormModalHeading from 'components/generic/modals/form-modal/FormModalHeading'
import Input from 'components/generic/form/Input'

// helpers and utils
import { defaultVehicleInformation, formatVehicleData } from 'components/containers/inbound-tickets/utils'
import { scrapVehicleFormOptions, formOptions } from 'utils/validation/vehicle'
import { formatVehicleLabel } from 'utils/data-manipulation/common'

const AddEditVehicleBlade = ({ onFormStateChange, onHideModal }) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { tab, vehicleData, inboundTicketData, inboundTicketId, showOnlyScrapVehicleTab } = useSelector(
    state => state.ui.formModalProps,
  )
  const [tabValue, setTabValue] = useState(tab)
  const isCustomerVehicleTab = tabValue === 'customer-vehicle'

  const setAllVehicleValues = (setValue, vehicleToUpdate) => {
    Object.keys(vehicleToUpdate).forEach(key => {
      setValue(`vehicle.${key}`, vehicleToUpdate[key])
    })
  }

  const { handleSubmit, control, reset, setValue, setError, formState, getValues, trigger, watch } = useForm(
    isCustomerVehicleTab ? formOptions : scrapVehicleFormOptions,
  )
  const { errors, isDirty } = formState
  const [shouldFetchVin, setShouldFetchVin] = useState(false)

  // queries and mutations
  const [createVehicle, { isLoading: isLoadingCreateVehicle }] = useCreateVehicleMutation()

  const {
    data: vehicleInfoData,
    isLoading: isLoadingVinData,
    isFetching,
  } = useGetVehicleInfoFromVinQuery(
    { vin: getValues('vehicle.vin') },
    {
      skip: !getValues('vehicle.vin') || !shouldFetchVin || errors?.vehicle?.vin,
    },
  )

  useEffect(() => {
    if (vehicleData) {
      setAllVehicleValues(setValue, vehicleData)
    } else {
      reset({ vehicle: defaultVehicleInformation })
    }
  }, [vehicleData])

  useEffect(() => {
    if (vehicleInfoData) {
      if (vehicleInfoData?.data) {
        const { data } = vehicleInfoData
        // temporary solution to address `KIA` vs `KIA MOTORS CORPORATION` issue (GREEN-8892)
        const formattedVehicleMake = data?.make === 'KIA' ? 'KIA MOTORS CORPORATION' : data?.make
        setValue('vehicle.make', formattedVehicleMake)
        setValue('vehicle.year', `${data?.year}`)
        setValue('vehicle.model', data?.model)
      } else {
        setError('vehicle.vin', {
          type: 'custom',
          message: 'VIN could not be found. Please check the VIN and try again.',
        })
      }
    }
  }, [vehicleInfoData])

  useEffect(() => {
    onFormStateChange(false)
  }, [])

  const isLoading = isLoadingCreateVehicle || isLoadingVinData || isFetching

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
    if (newValue === 'customer-vehicle') {
      reset({ vehicle: defaultVehicleInformation })
    }
  }

  const handleError = error =>
    dispatch(
      setSnackBar({
        snackbarType: 'error',
        snackbarMessage: error?.data?.error || 'There was an error saving this vehicle',
      }),
    )

  const handleSuccess = message => {
    dispatch(
      setSnackBar({
        snackbarType: 'success',
        snackbarMessage: message,
      }),
    )
    onHideModal(navigate)
  }

  const searchVinNumber = async () => {
    const isVinValid = await trigger('vehicle')

    if (isVinValid) {
      setShouldFetchVin(true)
    } else {
      setShouldFetchVin(false)
    }
  }

  const canSubmit = () => (isCustomerVehicleTab ? isDirty : !!getValues()?.vehicle?.vin)

  const onSubmit = async data => {
    const { vehicle: vehicleFormData } = data

    if (isCustomerVehicleTab) {
      await createVehicle({
        vehicle: formatVehicleData(vehicleFormData),
      })
        .unwrap()
        .then(vehicleCreated => {
          dispatch(
            setNewCustomerVehicle({
              ...vehicleCreated,
              label: formatVehicleLabel(vehicleCreated),
            }),
          )
          handleSuccess('Vehicle successfully saved')
        })
        .catch(error => handleError(error))
    } else if (!isCustomerVehicleTab && vehicleData && isDirty) {
      dispatch(
        updateScrapVehicle({
          ...vehicleFormData,
        }),
      )
      handleSuccess('Scrap vehicle updated')
    } else {
      dispatch(
        addScrapVehicle({
          ...vehicleFormData,
          registrationExpiration: Date.parse(vehicleFormData.registrationExpiration),
          rowId: uuidv4(),
        }),
      )
      handleSuccess('Scrap vehicle added')
    }
    onHideModal()
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {isLoading && <Loading />}
      <Stack height="100vh">
        <FormModalHeading
          title="Enter Vehicle Information"
          backButtonPosition="right"
          onBack={() => onHideModal(navigate)}
        />
        <Box px={3} sx={{ borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            {!showOnlyScrapVehicleTab && (
              <Tab sx={{ width: '240px' }} value="customer-vehicle" label="CUSTOMER VEHICLE" />
            )}
            <Tab sx={{ width: '240px' }} value="scrap-vehicle" label="SCRAP VEHICLE" />
          </Tabs>
        </Box>
        <Divider />
        <Box px={4} py={2} flexGrow={1}>
          <Stack flexGrow={1} data-testid={tabValue}>
            <Grid item md={7.5} xs={6}>
              {tabValue === 'scrap-vehicle' && (
                <Stack direction="row" spacing={2}>
                  <Input
                    name="vehicle.vin"
                    label="Vehicle Identification # (VIN)"
                    defaultValue=""
                    control={control}
                    error={!!errors?.vehicle?.vin}
                    helperText={errors?.vehicle?.vin?.message}
                  />
                  <Button
                    sx={{
                      whiteSpace: 'nowrap',
                      py: 2,
                      px: 3,
                      alignSelf: 'flex-start',
                    }}
                    data-testid="find-scrap-vehicle-button"
                    variant="outlined"
                    onClick={() => searchVinNumber()}
                  >
                    FIND VEHICLE
                  </Button>
                </Stack>
              )}
            </Grid>
            <VehicleFormLayout
              control={control}
              errors={errors}
              watch={watch}
              fieldPathPrefix="vehicle"
              isTabContent
              tab={tabValue}
            />
          </Stack>
        </Box>
        <Divider />
        <Stack direction="row" p={2} spacing={2}>
          <Button
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            disabled={!canSubmit() || !isDirty}
            data-testid="save-vehicle-information-button"
          >
            Save
          </Button>
          <Button
            variant="outlined"
            onClick={() => onHideModal(navigate)}
            data-testid="cancel-vehicle-information-button"
          >
            Cancel
          </Button>
        </Stack>
      </Stack>
    </form>
  )
}

AddEditVehicleBlade.propTypes = {
  onFormStateChange: PropTypes.func,
  onHideModal: PropTypes.func,
}

AddEditVehicleBlade.defaultProps = {
  onFormStateChange: noop,
  onHideModal: null,
}

export default AddEditVehicleBlade
